import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Input,
} from '@angular/core';
import { AnimationBehavior } from '@freelancer/directives';
import { ContainerSize } from '../container';
import { VerticalAlignment } from '../grid';
import { HeadingColor, HeadingType } from '../heading';
import { IconColor } from '../icon';
import {
  LinkColor,
  LinkHoverColor,
  LinkIconPosition,
  LinkWeight,
} from '../link';
import type { PictureBackgroundImageStyles } from '../picture';
import { PictureBgImageObjectFit } from '../picture';
import { FontColor, FontWeight, TextSize } from '../text';

@Component({
  selector: 'fl-section-homepage-feature-title',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHomepageFeatureTitleComponent {}

@Component({
  selector: 'fl-section-homepage-feature-top-left',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHomepageFeatureTopLeftComponent {}

@Component({
  selector: 'fl-section-homepage-feature-top-right',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHomepageFeatureTopRightComponent {}

@Component({
  selector: 'fl-section-homepage-feature-bottom-left',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHomepageFeatureBottomLeftComponent {}

@Component({
  selector: 'fl-section-homepage-feature-bottom-right',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHomepageFeatureBottomRightComponent {}
@Component({
  selector: 'fl-section-homepage-feature-footer',
  template: `
    <p class="text-light text-mid desktop:text-xlarge font-bold">
      {{ footerCopy }}
    </p>
    <fl-link
      flTrackingLabel="flFooterTrackingLabel"
      [color]="LinkColor.PRIMARY"
      [iconColor]="IconColor.PRIMARY"
      [iconName]="'ui-arrow-right'"
      [iconPosition]="LinkIconPosition.RIGHT"
      [hoverColor]="LinkHoverColor.PRIMARY"
      [link]="footerUrl"
      [size]="TextSize.MID"
      [weight]="LinkWeight.BOLD"
    >
      {{ footerLink }}
    </fl-link>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHomepageFeatureFooterComponent {
  FontColor = FontColor;
  FontWeight = FontWeight;
  IconColor = IconColor;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LinkIconPosition = LinkIconPosition;
  LinkWeight = LinkWeight;
  TextSize = TextSize;

  @Input() footerCopy: string;
  @Input() footerLink: string;
  @Input() footerUrl: string;
  @Input() flFooterTrackingLabel: string;
}

@Component({
  selector: 'fl-section-homepage-feature',
  template: `
    <fl-container
      [size]="ContainerSize.DESKTOP_LARGE"
      [sizeDesktopXLarge]="ContainerSize.DESKTOP_XLARGE"
      class="AnimationContainer"
    >
      <div
        class="AnimatedLineTop"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.GROW"
        [flAnimateOnScrollDelay]="0.5"
        [flAnimateOnScrollDuration]="0.3"
      ></div>
      <div
        class="AnimatedLogo"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
        [flAnimateOnScrollDuration]="0.2"
        [flAnimateOnScrollDelay]="0.8"
      ></div>
      <div
        class="AnimatedLineBottom"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.GROW"
        [flAnimateOnScrollDelay]="0.8"
        [flAnimateOnScrollDuration]="0.5"
      ></div>
    </fl-container>
    <fl-container
      class="Container"
      [size]="ContainerSize.DESKTOP_LARGE"
      [sizeDesktopXLarge]="ContainerSize.DESKTOP_XLARGE"
    >
      <fl-picture
        [isBackgroundImage]="true"
        [srcTablet]="srcBackgroundTablet"
        [srcLargeDesktop]="srcBackgroundLargeDesktop"
        [backgroundStyles]="backgroundStyles"
        [flHideMobile]="true"
      >
      </fl-picture>
      <div class="Wrapper">
        <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
          <fl-col
            [col]="12"
            [colTablet]="6"
            [colDesktopLarge]="12"
            [flAnimateOnScroll]="true"
            [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
            [flAnimateOnScrollDuration]="0.3"
          >
            <fl-heading
              [color]="HeadingColor.LIGHT"
              [headingType]="HeadingType.H2"
              [size]="TextSize.XXLARGE"
              [sizeDesktop]="TextSize.MARKETING_MEGA"
              [flMarginBottom]="'mid'"
              [flMarginBottomTablet]="'large'"
              [flMarginBottomDesktop]="'xlarge'"
            >
              <ng-content
                select="fl-section-homepage-feature-title"
              ></ng-content>
            </fl-heading>
          </fl-col>
          <fl-col
            class="TableCardContainer"
            [col]="12"
            [colTablet]="6"
            [flHideMobile]="true"
            [flHideDesktopLarge]="true"
          >
            <fl-picture
              class="TableCard"
              [srcTablet]="srcImageAnimationTablet"
              [boundedWidth]="true"
              [flAnimateOnScroll]="true"
              [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
              [flAnimateOnScrollDuration]="0.3"
              [flAnimateOnScrollDelay]="0.3"
            >
            </fl-picture>
          </fl-col>
        </fl-grid>
        <fl-grid>
          <fl-col
            [col]="12"
            [colDesktopLarge]="6"
            [flMarginBottom]="'large'"
            [flMarginBottomTablet]="'none'"
            [flAnimateOnScroll]="true"
            [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
            [flAnimateOnScrollDuration]="0.3"
            [flAnimateOnScrollDelay]="0.1"
          >
            <fl-grid>
              <fl-col
                [col]="12"
                [colTablet]="6"
              >
                <ng-content
                  select="fl-section-homepage-feature-top-left"
                ></ng-content>
              </fl-col>
              <fl-col
                [col]="12"
                [colTablet]="6"
              >
                <ng-content
                  select="fl-section-homepage-feature-top-right"
                ></ng-content>
              </fl-col>
            </fl-grid>
            <fl-grid
              [flMarginBottom]="
                sectionHomepageFeatureFooterComponent ? 'xxsmall' : 'none'
              "
              [flMarginBottomTablet]="
                sectionHomepageFeatureFooterComponent ? 'small' : 'none'
              "
            >
              <fl-col
                [col]="12"
                [colTablet]="6"
              >
                <ng-content
                  select="fl-section-homepage-feature-bottom-left"
                ></ng-content>
              </fl-col>
              <fl-col
                [col]="12"
                [colTablet]="6"
              >
                <ng-content
                  select="fl-section-homepage-feature-bottom-right"
                ></ng-content>
              </fl-col>
            </fl-grid>
            <ng-container *ngIf="sectionHomepageFeatureFooterComponent">
              <ng-content
                select="fl-section-homepage-feature-footer"
              ></ng-content>
            </ng-container>
          </fl-col>
          <fl-col
            class="AnimatedImage"
            [col]="12"
            [colDesktopLarge]="6"
          >
            <fl-picture
              class="MobileCard"
              [src]="srcImageAnimationMobile"
              [srcLargeDesktop]="srcImageAnimationLargeDesktop"
              [boundedWidth]="true"
              [flAnimateOnScroll]="true"
              [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
              [flAnimateOnScrollDelay]="0.3"
              [flAnimateOnScrollDuration]="0.3"
            >
            </fl-picture>

            <fl-picture
              [flShowMobile]="true"
              [src]="srcBackgroundMobile"
            >
            </fl-picture>
          </fl-col>
        </fl-grid>
      </div>
    </fl-container>
  `,
  styleUrls: ['./section-homepage-feature.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionHomepageFeatureComponent {
  AnimationBehavior = AnimationBehavior;
  ContainerSize = ContainerSize;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  FontColor = FontColor;
  FontWeight = FontWeight;
  TextSize = TextSize;
  VerticalAlignment = VerticalAlignment;

  readonly backgroundStyles: PictureBackgroundImageStyles = {
    position: 'center top',
    positionDesktop: 'center',
    objectFitTablet: PictureBgImageObjectFit.CONTAIN,
    objectFitDesktop: PictureBgImageObjectFit.NONE,
    objectFitDesktopLarge: PictureBgImageObjectFit.NONE,
  };

  @Input() srcImageAnimationMobile: string;
  @Input() srcImageAnimationTablet: string;
  @Input() srcImageAnimationLargeDesktop: string;

  @Input() srcBackgroundMobile: string;
  @Input() srcBackgroundTablet: string;
  @Input() srcBackgroundLargeDesktop: string;

  @ContentChild(SectionHomepageFeatureFooterComponent)
  sectionHomepageFeatureFooterComponent: SectionHomepageFeatureFooterComponent;
}
